import { memo } from 'react';
import { ReactComponent as MapIcon } from '@assets/icons/map-marker-alt-solid.svg';

import {
  Flex,
  FlexProps,
  HStack,
  useBoolean,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { formatDistance, getDistanceInMiles, useGeolocation } from 'utils';

import { Button, Text } from 'ui';

interface ShipmentMethodItemProps extends FlexProps {
  label: string;
  description?: string;
  cost?: string;
  onClick: () => void;
  address?: string[];
  isIcon?: boolean;
  isActive?: boolean;
  isHoverAvailable?: boolean;
  itemLongitude?: number | null;
  itemLatitude?: number | null;
}
const ShipmentMethodItem = ({
  label,
  description,
  cost,
  onClick,
  address = [],
  isIcon = false,
  isActive = false,
  isHoverAvailable = false,
  itemLongitude = 0,
  itemLatitude = 0,
  ...props
}: ShipmentMethodItemProps) => {
  const { geoLocation } = useGeolocation();
  const {
    colors: {
      palette: {
        shop: {
          cart: { shippingStep: styles }
        }
      }
    }
  } = useTheme();
  const [isHovered, setIsHovered] = useBoolean(false);

  const userLatitude = geoLocation?.latitude;
  const userLongitude = geoLocation?.longitude;
  const shouldDisplayDistance =
    userLatitude && userLongitude && itemLongitude && itemLatitude;

  const activeLabel = isActive ? 'Selected' : 'Select';

  const distance = shouldDisplayDistance
    ? formatDistance(
        getDistanceInMiles(geoLocation, {
          longitude: itemLongitude,
          latitude: itemLatitude
        })
      )
    : '';

  return (
    <Flex
      w='full'
      bg={styles.methodBg}
      borderRadius='6px'
      px={{ base: '16px', md: '30px' }}
      py={address.length > 0 ? '22px' : 0}
      h={address.length > 0 ? 'auto' : 95}
      justifyContent='space-between'
      alignItems='center'
      {...props}
    >
      <HStack gap={{ base: '14px', md: '28px' }}>
        <Button
          onMouseEnter={setIsHovered.on}
          onMouseLeave={setIsHovered.off}
          colorScheme='primaryLight'
          variant='outline'
          onClick={onClick}
          lineHeight='18px'
          fontSize='13px'
          minH='35px'
          w='106px'
          bgColor={styles.bg}
          py='9px'
          px='31px'
          isActive={isActive}
          _active={{
            background: styles.activeBg,
            borderColor: styles.activeBorder,
            color: styles.activeColor
          }}
        >
          {isHovered && isActive && isHoverAvailable ? 'change' : activeLabel}
        </Button>

        <VStack alignItems='flex-start'>
          {label && (
            <Text fontSize='21px' color={styles.textColor}>
              {label}
            </Text>
          )}
          {description && (
            <Text
              fontSize={{ base: '12px', md: '14px' }}
              color={styles.textColor}
            >
              {description}
            </Text>
          )}
          {address.length > 0 && (
            <VStack alignItems='flex-start' gap='4px'>
              {address.map((str) => (
                <Text
                  key={str}
                  fontSize={{ base: '14px', md: '16px' }}
                  color={styles.textColor}
                >
                  {str}
                </Text>
              ))}
            </VStack>
          )}
        </VStack>
      </HStack>

      {cost && (
        <Text
          fontSize={{ base: '14px', sm: '22px', lg: '24px' }}
          color={styles.textColor}
          lineHeight='1.3'
          fontWeight={{ base: '400', sm: '600' }}
          textAlign={{ base: 'center', sm: 'left' }}
        >
          {cost}
        </Text>
      )}

      {isIcon && (
        <Flex>
          <Text
            fontSize={{ base: '14px', md: '16px' }}
            color={styles.textColor}
            textTransform='uppercase'
            textAlign='center'
            mr={2}
          >
            {distance}
          </Text>
          <MapIcon fill={styles.iconColor} width={21} height={21} />
        </Flex>
      )}
    </Flex>
  );
};

export default memo(ShipmentMethodItem);
